import { Alert, Box, Button, Chip, Container, Dialog, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Formik } from 'formik';
import React, { useState } from 'react';
import * as yup from 'yup';
import { Medication, PatientSummary } from '../../store/patientsSlice.ts';

import * as Yup from 'yup';
import { getPatientStatusDescription } from '../../common/enums.tsx';

// Validation schema for the password form
export const passwordValidationSchema = Yup.string()
  .required('Requis')
  .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
  .matches(/[0-9]/, 'Le mot de passe doit contenir au moins un chiffre')
  .matches(/[A-Z]/, 'Le mot de passe doit contenir au moins une lettre majuscule');

interface InfoBoxProps {
  emoji: string;
  children: React.ReactNode;
}

const InfoBox: React.FC<InfoBoxProps> = ({ emoji, children }) => {
  return (
    <Alert icon={false} sx={{ mb: 2, bgcolor: 'primary.light', color: 'primary.main', fontSize: '1rem' }} variant="filled">
      <Stack direction="row" spacing={2}>
        <div>{emoji}</div>
        <div>{children}</div>
      </Stack>
    </Alert>
  );
};

export default InfoBox;

interface PageBoxProps {
  title: string;
  children: React.ReactNode;
}
export const PageBox: React.FC<PageBoxProps> = ({ title, children }) => {
  return (
    <Box
      sx={{
        border: '3px solid',
        borderColor: 'primary.main',
        borderRadius: 4,
        p: 3,
        position: 'relative',
        zIndex: 1,
        mt: 5,
      }}
    >
      <Typography
        align="center"
        component="h1"
        sx={{ ml: 25, mr: 25, mt: -4, mb: 4, bgcolor: 'background.paper', color: 'primary.main', fontWeight: 'bold' }}
        variant="h5"
      >
        {title}
      </Typography>
      <Container>{children}</Container>
    </Box>
  );
};
const defaultMedication: Medication = {
  id: undefined,
  startDate: '',
  name: '',
  quantity: 0,
  unit: '',
  frequency: '',
};

const unitOptions = ['mg', 'g', 'ml', 'goutte'];
const frequencyOptions = [
  'Tous les jours',
  'Un jour sur deux',
  'Toutes les semaines',
  'Toutes les deux semaines',
  'Toutes les mois',
  'Tous les 3 mois',
  'Tous les 6 mois',
  'Autre',
];
interface MedicationEditionProps {
  medications: Medication[];
  onMedicationsChange: (medications: Medication[]) => void;
}

export const MedicationEdition: React.FC<MedicationEditionProps> = ({ medications, onMedicationsChange }) => {
  const [showMedicationModal, setShowMedicationModal] = useState(false);
  const [newMedication, setNewMedication] = useState<Medication>(defaultMedication);

  const handleShowMedicationModal = () => {
    setShowMedicationModal(true);
  };
  const handleCloseMedicationModal = () => {
    setShowMedicationModal(false);
  };

  const handleAddMedication = (medication: Medication) => {
    medications = [...medications, { ...medication, id: Date.now().toString() }];
    onMedicationsChange(medications);
    handleCloseMedicationModal();
    setNewMedication(defaultMedication);
  };

  const removeMedication = (id: string | undefined) => {
    medications = medications.filter((med) => med.id !== id);
    onMedicationsChange(medications); //    formik.setFieldValue("medications", updatedMedications);
  };

  const dialogValidationSchema = yup.object().shape({
    startDate: yup.string().required('Date de début est requise'),
    name: yup.string().required('Nom du médicament est requis'),
    quantity: yup.number().required('Quantité est requise').positive('Quantité doit être positive'),
    unit: yup.string().required('Unité est requise'),
    frequency: yup.string().required('Fréquence est requise'),
  });

  return (
    <Stack spacing={2}>
      {medications.map((med) => (
        <Chip
          key={med.id}
          label={`${med.name} ${med.quantity.toString()} ${med.unit}, ${med.frequency} x par jour, débuté en ${format(new Date(med.startDate), 'MMM yy')}.`}
          onDelete={() => {
            removeMedication(med.id);
          }}
          size="small"
          sx={{ color: 'primary.main', backgroundColor: 'primary.light', mb: 1, fontWeight: 'medium', borderColor: 'primary.main' }}
          variant="outlined"
        />
      ))}
      <Button onClick={handleShowMedicationModal} variant="outlined">
        + Ajouter un traitement
      </Button>

      <Dialog maxWidth="lg" onClose={handleCloseMedicationModal} open={showMedicationModal}>
        <DialogTitle>Ajouter un traitement en cours (tous types de traitements confondus)</DialogTitle>
        <DialogContent>
          <Formik initialValues={newMedication} onSubmit={handleAddMedication} validationSchema={dialogValidationSchema}>
            {(insideFormik) => (
              <form onSubmit={insideFormik.handleSubmit}>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid size={2}>
                    <LocalizationProvider adapterLocale={fr} dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Date de début"
                        name="startDate"
                        onChange={(value) => {
                          void insideFormik.setFieldValue('startDate', value);
                        }}
                        slotProps={{
                          textField: {
                            error: insideFormik.touched.startDate && Boolean(insideFormik.errors.startDate),
                            helperText: insideFormik.touched.startDate && insideFormik.errors.startDate,
                            onBlur: insideFormik.handleBlur,
                            fullWidth: true,
                            variant: 'outlined',
                            margin: 'normal',
                          },
                        }}
                        value={insideFormik.values.startDate !== '' ? new Date(insideFormik.values.startDate) : null}
                        views={['month', 'year']}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid size={2}>
                    <TextField
                      error={insideFormik.touched.name && !!insideFormik.errors.name}
                      fullWidth
                      helperText={insideFormik.touched.name && insideFormik.errors.name}
                      label="Médicament"
                      margin="normal"
                      name="name"
                      onBlur={insideFormik.handleBlur}
                      onChange={insideFormik.handleChange}
                      placeholder="Nom du médicament"
                      value={insideFormik.values.name}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={2}>
                    <TextField
                      error={insideFormik.touched.quantity && !!insideFormik.errors.quantity}
                      fullWidth
                      helperText={insideFormik.touched.quantity && insideFormik.errors.quantity}
                      label="Quantité totale journalière"
                      margin="normal"
                      name="quantity"
                      onBlur={insideFormik.handleBlur}
                      onChange={insideFormik.handleChange}
                      placeholder="Quantité"
                      type="number"
                      value={insideFormik.values.quantity}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={2}>
                    <TextField
                      error={insideFormik.touched.unit && !!insideFormik.errors.unit}
                      fullWidth
                      helperText={insideFormik.touched.unit && insideFormik.errors.unit}
                      label="Unité"
                      margin="normal"
                      name="unit"
                      onBlur={insideFormik.handleBlur}
                      onChange={insideFormik.handleChange}
                      select
                      value={insideFormik.values.unit}
                      variant="outlined"
                    >
                      {unitOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={2}>
                    <TextField
                      error={insideFormik.touched.frequency && !!insideFormik.errors.frequency}
                      fullWidth
                      helperText={insideFormik.touched.frequency && insideFormik.errors.frequency}
                      label="Fréquence journalière"
                      margin="normal"
                      name="frequency"
                      onBlur={insideFormik.handleBlur}
                      onChange={insideFormik.handleChange}
                      select
                      value={insideFormik.values.frequency}
                      variant="outlined"
                    >
                      {frequencyOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid justifyContent="flex-end" size={12}>
                    <Button color="secondary" onClick={handleCloseMedicationModal}>
                      Annuler
                    </Button>
                    <Button sx={{ float: 'right' }} type="submit" variant="contained">
                      Ajouter
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};
interface PatientSummaryProps {
  patient: PatientSummary;
}

export const PatientStatusChip: React.FC<PatientSummaryProps> = ({ patient }) => {
  const getChipProps = (status: string) => {
    const label = getPatientStatusDescription(status);
    switch (status) {
      case 'CREATED':
        return { label: label, color: 'default' as const };
      case 'INCLUDED':
        return { label: label, color: 'primary' as const };
      case 'ACTIVATED':
        return { label: label, color: 'secondary' as const };
      case 'CLOSED':
        return { label: label, color: 'default' as const };
      default:
        return { label: label, color: 'default' as const };
    }
  };
  const chipProps = getChipProps(patient.status);

  return <Chip {...chipProps} />;
};
